import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import type { PostalAddressInput } from '__generated__/globalTypes';
import { ButtonBase } from 'atoms/buttons/ButtonBase';
import { NativeSelect } from 'atoms/inputs/NativeSelect';
import { Vertical } from 'atoms/layout/flex';
import { LoadingIndicator } from 'atoms/loader/LoadingIndicator';
import { BodyM } from 'atoms/typography';
import {
  PopoverContent,
  PopoverPortal,
  PopoverRoot,
  PopoverTrigger,
} from 'atoms/ui/Popover';
import type { GraphQLResult, SubmitButtonProps } from 'components/form/Form';
import { GraphqlForm, TextField } from 'components/form/Form';
import { Field } from 'components/form/Form/Field';
import { GOOGLE_MAPS_API_KEY } from 'config';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useQuery } from 'hooks/graphql/useQuery';
import { glossary } from 'lib/glossary';
import { loadScript } from 'lib/script';

import type {
  DeliverableCountriesQuery,
  DeliverableCountriesQueryVariables,
  PostalAddressForm_userSettings,
} from './__generated__/index.graphql';

const DELIVERABLE_COUNTRIES_QUERY = gql`
  query DeliverableCountriesQuery {
    config {
      id
      deliverableCountries {
        slug
        name
      }
    }
  }
` as TypedDocumentNode<
  DeliverableCountriesQuery,
  DeliverableCountriesQueryVariables
>;
const LoadingWrapper = styled.div`
  padding: var(--double-unit);
`;

const StyledGraphqlForm = styled(GraphqlForm)`
  margin-bottom: 0;
`;

const FormWrapper = styled.div`
  width: 100%;
  display: grid;
  gap: var(--unit);
  grid-template-areas:
    'first_name last_name'
    'country country'
    'address address'
    'additional_address additional_address'
    'zip city'
    'region region'
    'cta cta';
  grid-template-columns: repeat(2, 1fr);
`;
const Root = styled(Vertical).attrs({ gap: 2 })`
  width: 100%;
`;
const Country = styled.div`
  grid-area: country;
`;
const FirstName = styled.div`
  grid-area: first_name;
`;
const LastName = styled.div`
  grid-area: last_name;
`;
const Address = styled.div`
  grid-area: address;
`;
const AdditionalAddress = styled.div`
  grid-area: additional_address;
`;
const ZipCode = styled.div`
  grid-area: zip;
`;
const City = styled.div`
  grid-area: city;
`;

const Region = styled.div`
  grid-area: region;
`;

const messages = defineMessages({
  address: {
    id: 'ItemPreviewDialog.PostalAddressForm.address',
    defaultMessage: 'Address',
  },
  additionalAddress: {
    id: 'ItemPreviewDialog.PostalAddressForm.additionalAddress',
    defaultMessage: 'Additional Address',
  },
  city: {
    id: 'ItemPreviewDialog.PostalAddressForm.city',
    defaultMessage: 'City',
  },
  country: {
    id: 'ItemPreviewDialog.PostalAddressForm.country',
    defaultMessage: 'Country',
  },
  region: {
    id: 'ItemPreviewDialog.PostalAddressForm.region',
    defaultMessage: 'Region',
  },
});

export type Attributes = {
  additionalAddress?: string | null;
  city?: string | null;
  countryCode?: string | null;
  country?: { name: string; slug: string } | null;
  firstName?: string | null;
  lastName?: string | null;
  streetAddress: string | null;
  zipcode: string | null;
  region?: string | null;
};
type Props = {
  onSubmit: (
    attributes: PostalAddressInput,
    onResult: (result: GraphQLResult) => void,
    onCancel: () => void
  ) => void;
  onSuccess: () => void;
  onChange?: (attributes: Attributes) => void;
  button: (
    SubmitButton: React.ComponentType<
      React.PropsWithChildren<SubmitButtonProps>
    >
  ) => ReactNode;
  defaultValues?: Attributes;
  hiddenFields?: Array<keyof Attributes>;
};

const StyledPopoverContent = styled(PopoverContent)`
  background-color: var(--c-black);
  width: var(--radix-popover-trigger-width);
  border-radius: var(--double-unit);
  overflow: hidden;
`;

const StyledButtonBase = styled(ButtonBase)`
  padding: var(--unit);

  &:hover {
    background-color: var(--c-nd-100);
  }
`;

const StyledNativeSelect = styled(NativeSelect)`
  display: block;
`;

export const PostalAddressForm = ({
  onSubmit,
  onSuccess,
  onChange,
  button,
  defaultValues: defaultValuesFromProps,
  hiddenFields = ['region'],
}: Props) => {
  const [lastSuggestionSelected, setLastSuggestionSelected] = useState<
    string | undefined
  >();
  const { currentUser } = useCurrentUserContext();
  const tokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(
    null
  );
  const [values, setValues] = useState<Attributes | undefined>(
    defaultValuesFromProps || currentUser?.userSettings.postalAddress
  );

  const [initialOnChangeCalled, setInitialOnChangeCalled] = useState(false);
  const [placeSuggestions, setPlaceSuggestions] = useState<
    google.maps.places.PlacePrediction[]
  >([]);

  const { formatMessage } = useIntl();
  const { data, loading } = useQuery(DELIVERABLE_COUNTRIES_QUERY);
  const countriesOptions = [
    {
      label: formatMessage({
        id: 'ShopItemPicker.ItemPreviewDialog.PostalAddressForm.countryDefaultOption',
        defaultMessage: 'Choose a country',
      }),
      value: '',
    },
    ...(data?.config.deliverableCountries || [])
      .map(country => ({
        label: country.name,
        value: country.slug,
      }))
      .sort((a, b) => (a.label > b.label ? 1 : -1)),
  ];

  const [countryCode, setCountryCode] = useState<string | undefined>(
    values?.countryCode || ''
  );

  const refreshToken = () => {
    tokenRef.current = new google.maps.places.AutocompleteSessionToken();
  };

  useEffect(() => {
    async function loadGoogleMapsScript() {
      await loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`
      );
      refreshToken();
    }
    loadGoogleMapsScript();
  }, []);

  const makeAutocompleteRequest = async (input: string) => {
    if (!input?.trim() || !tokenRef.current) {
      setPlaceSuggestions([]);
      return;
    }
    const request: google.maps.places.AutocompleteRequest = {
      input,
      includedPrimaryTypes: ['street_address'],
      sessionToken: tokenRef.current,
      region: countryCode,
    };
    const { suggestions } =
      await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
        request
      );

    if (!suggestions) return;
    setPlaceSuggestions(
      suggestions.map(suggestion => suggestion.placePrediction).filter(Boolean)
    );
  };

  const onPlaceSelected = async (place: google.maps.places.PlacePrediction) => {
    const placeData = await place.toPlace().fetchFields({
      fields: ['displayName', 'addressComponents'],
    });

    const { addressComponents, displayName } = placeData.place;

    const country = addressComponents?.find(component =>
      component.types.includes('country')
    );

    const newValues = {
      streetAddress: displayName || '',
      zipcode:
        addressComponents?.find(component =>
          component.types.includes('postal_code')
        )?.longText || '',
      city:
        addressComponents?.find(component =>
          component.types.includes('locality')
        )?.longText || '',
      countryCode: (country?.shortText || '').toLowerCase(),
      country: {
        name: country?.longText || '',
        slug: (country?.shortText || '').toLowerCase(),
      },
      region:
        addressComponents?.find(component =>
          component.types.includes('administrative_area_level_1')
        )?.longText || '',
    };

    setValues(prev => ({ ...prev, ...newValues }));

    setLastSuggestionSelected(displayName || undefined);
    setPlaceSuggestions([]);
    refreshToken();
  };

  if (!currentUser) {
    return null;
  }

  const onFormChange = (input: Attributes) => {
    setValues(prev => ({ ...prev, ...input }));
    onChange?.(input);
  };

  if (!initialOnChangeCalled && values) {
    onChange?.(values);
    setInitialOnChangeCalled(true);
  }

  return (
    <StyledGraphqlForm
      key={lastSuggestionSelected}
      onSubmit={onSubmit}
      onChange={onFormChange}
      onSuccess={onSuccess}
      render={(
        Error: React.ComponentType<React.PropsWithChildren<unknown>>,
        SubmitButton: React.ComponentType<
          React.PropsWithChildren<SubmitButtonProps>
        >
      ) => (
        <Root>
          <FormWrapper>
            <Country>
              {countriesOptions.length && !loading ? (
                <Field
                  name="countryCode"
                  defaultValue={
                    (
                      countriesOptions.find(
                        ({ value }) => value === values?.countryCode
                      ) || countriesOptions[0]
                    )?.value
                  }
                  render={({ error, handleChange, value }) => {
                    return (
                      <>
                        <BodyM
                          bold
                          color="currentColor"
                          as="label"
                          htmlFor="countryCode"
                        >
                          {formatMessage(messages.country)}
                        </BodyM>
                        <StyledNativeSelect
                          value={value || ''}
                          onChange={e => {
                            setCountryCode(e);
                            setPlaceSuggestions([]);
                            handleChange(e);
                          }}
                          values={countriesOptions}
                        />
                        {!!error && (
                          <BodyM color="var(--c-red-400)">{error}</BodyM>
                        )}
                      </>
                    );
                  }}
                />
              ) : (
                <LoadingWrapper>
                  <LoadingIndicator small />
                </LoadingWrapper>
              )}
            </Country>
            {!hiddenFields.includes('firstName') && (
              <FirstName>
                <TextField
                  name="firstName"
                  defaultValue={values?.firstName || ''}
                  label={formatMessage(glossary.firstName)}
                  required
                />
              </FirstName>
            )}
            {!hiddenFields.includes('lastName') && (
              <LastName>
                <TextField
                  name="lastName"
                  defaultValue={values?.lastName || ''}
                  label={formatMessage(glossary.lastName)}
                  required
                />
              </LastName>
            )}
            {!hiddenFields.includes('streetAddress') && (
              <PopoverRoot open>
                <PopoverTrigger asChild>
                  <Address>
                    <TextField
                      id="autocomplete"
                      name="streetAddress"
                      defaultValue={values?.streetAddress || ''}
                      label={formatMessage(messages.address)}
                      onChange={e => {
                        makeAutocompleteRequest(e.target.value);
                      }}
                      required
                    />
                  </Address>
                </PopoverTrigger>
                <PopoverPortal>
                  <StyledPopoverContent>
                    <Vertical gap={0}>
                      {placeSuggestions.map(suggestion => (
                        <StyledButtonBase
                          key={suggestion.text.toString()}
                          onClick={() => {
                            onPlaceSelected(suggestion);
                          }}
                        >
                          {suggestion.text.toString()}
                        </StyledButtonBase>
                      ))}
                    </Vertical>
                  </StyledPopoverContent>
                </PopoverPortal>
              </PopoverRoot>
            )}
            {!hiddenFields.includes('additionalAddress') && (
              <AdditionalAddress>
                <TextField
                  name="additionalAddress"
                  defaultValue={values?.additionalAddress || ''}
                  label={formatMessage(messages.additionalAddress)}
                />
              </AdditionalAddress>
            )}
            {!hiddenFields.includes('zipcode') && (
              <ZipCode>
                <TextField
                  name="zipcode"
                  defaultValue={values?.zipcode || ''}
                  label={formatMessage(glossary.zipCode)}
                  required
                />
              </ZipCode>
            )}
            {!hiddenFields.includes('city') && (
              <City>
                <TextField
                  name="city"
                  defaultValue={values?.city || ''}
                  label={formatMessage(messages.city)}
                  required
                />
              </City>
            )}
            {!hiddenFields.includes('region') && (
              <Region>
                <TextField
                  name="region"
                  defaultValue={values?.region || ''}
                  label={formatMessage(messages.region)}
                />
              </Region>
            )}
          </FormWrapper>
          {button(SubmitButton)}
        </Root>
      )}
    />
  );
};

PostalAddressForm.fragments = {
  userSettings: gql`
    fragment PostalAddressForm_userSettings on UserSettings {
      id
      postalAddress {
        firstName
        lastName
        streetAddress
        additionalAddress
        zipcode
        city
        countryCode
        country {
          slug
          name
        }
      }
    }
  ` as TypedDocumentNode<PostalAddressForm_userSettings>,
};
